import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from '@fluentui/react-components';
import {
  AddCircle24Regular,
  ArrowClockwiseDashesColor,
  DeleteRegular,
  Edit24Regular,
} from '@fluentui/react-icons';
import { getApiErrorMessage, type IPagedCollection } from '@moko/utils';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { appRoles } from '../../authConfig';
import {
  DAY_OF_WEEK,
  deleteContractorAvailabilityRule,
  getContractorAvailabilityRules,
  isValidDayOfWeek,
  isValidDaysOfWeek,
  type ContractorAvailabilityDraft,
  type IContractorAvailabilityRule,
  type ICreateContractorAvailabilityRuleRequest,
} from '../../services/contractorAvailabilityRuleService';
import { formatDateOnlyForDisplay } from '../../utils/dateOnly';
import ContractorAvailabilityDrawer from './ContractorAvailabilityDrawer';

const PAGE_SIZE = 10;

const labelForDay = (
  value: number,
  t: (key: string, fallback: string) => string
) => {
  const day = DAY_OF_WEEK.find((option) => option.value === value);
  return day ? t(day.key, day.fallback) : String(value);
};

type ContractorAvailabilityListItem = (
  | IContractorAvailabilityRule
  | (ContractorAvailabilityDraft & { contractorId: number })
) & {
  [key: string]: unknown;
};

type DrawerState =
  | { mode: 'create'; id: null }
  | { mode: 'edit'; id: number }
  | null;

const toListItem = (
  rule: ContractorAvailabilityDraft
): ContractorAvailabilityListItem => ({
  ...rule,
  contractorId: 0,
});

const pageDraft = (
  rules: ContractorAvailabilityDraft[],
  pageSize: number,
  pageNo: number
): IPagedCollection<ContractorAvailabilityListItem> => {
  const start = (pageNo - 1) * pageSize;
  const items = rules.slice(start, start + pageSize).map(toListItem);
  const rowsCount = rules.length;
  return {
    isLastPage: start + pageSize >= rowsCount,
    items,
    pageCount: rowsCount > 0 ? Math.ceil(rowsCount / pageSize) : 0,
    pageNo,
    pageSize,
    rowsCount,
  };
};

export interface ContractorAvailabilityProps {
  contractorId?: number;
  draftRules?: ContractorAvailabilityDraft[];
  onDraftChange?: (rules: ContractorAvailabilityDraft[]) => void;
}

const ContractorAvailability = ({
  contractorId,
  draftRules = [],
  onDraftChange,
}: ContractorAvailabilityProps) => {
  const { t } = useTranslation();
  const profileData = useAtomValue(profileDataAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const canEdit = profileData.roles.includes(appRoles.Admin);
  const isDraft = contractorId == null;

  const columns: DataGridColumn<ContractorAvailabilityListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        name: t('id', 'Id'),
        width: 70,
        isSortable: true,
      },
      {
        key: 'dayOfWeek',
        name: t('dayOfWeek', 'Day of week'),
        width: 140,
        isSortable: true,
        renderCell: (item) => {
          if ('daysOfWeek' in item && isValidDaysOfWeek(item.daysOfWeek)) {
            if (item.daysOfWeek.length === 0) return '-';
            return item.daysOfWeek
              .map((value) => labelForDay(value, t))
              .join(', ');
          }
          if ('dayOfWeek' in item && isValidDayOfWeek(item.dayOfWeek)) {
            return labelForDay(item.dayOfWeek, t);
          }
          return '-';
        },
      },
      {
        key: 'startTime',
        name: t('startTime', 'Start time'),
        width: 110,
        isSortable: true,
      },
      {
        key: 'endTime',
        name: t('endTime', 'End time'),
        width: 110,
        isSortable: true,
      },
      {
        key: 'timezoneId',
        name: t('timezone', 'Timezone'),
        width: 180,
        isSortable: true,
      },
      {
        key: 'effectiveFrom',
        name: t('effectiveFrom', 'Effective from'),
        width: 170,
        isSortable: true,
        renderCell: (item) => formatDateOnlyForDisplay(item.effectiveFrom),
      },
      {
        key: 'effectiveTo',
        name: t('effectiveTo', 'Effective to'),
        width: 170,
        isSortable: true,
        renderCell: (item) => formatDateOnlyForDisplay(item.effectiveTo),
      },
      {
        key: 'status',
        name: t('status', 'Status'),
        width: 100,
        renderCell: (item) =>
          item.status ? t('active', 'Active') : t('inactive', 'Inactive'),
      },
    ],
    [t]
  );

  const openCreate = useCallback(() => {
    setDrawer({ mode: 'create', id: null });
  }, []);

  const openEdit = useCallback((id: number) => {
    setDrawer({ mode: 'edit', id });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawer(null);
  }, []);

  const refreshList = useCallback(() => {
    gridRef.current?.refresh();
  }, []);

  const handleSaved = useCallback(() => {
    setSuccessMessage(
      drawer?.mode === 'create'
        ? t('availabilityCreated', 'Availability rule created.')
        : t('availabilityUpdated', 'Availability rule updated.')
    );
    closeDrawer();
    refreshList();
  }, [closeDrawer, drawer?.mode, refreshList, setSuccessMessage, t]);

  const handleDraftCreate = useCallback(
    (data: Omit<ICreateContractorAvailabilityRuleRequest, 'contractorId'>) => {
      const nextId =
        draftRules.reduce((max, rule) => Math.max(max, rule.id), 0) + 1;
      onDraftChange?.([...draftRules, { id: nextId, ...data }]);
      handleSaved();
    },
    [draftRules, handleSaved, onDraftChange]
  );

  const handleDraftUpdate = useCallback(
    (data: ContractorAvailabilityDraft) => {
      onDraftChange?.(
        draftRules.map((rule) => (rule.id === data.id ? data : rule))
      );
      handleSaved();
    },
    [draftRules, handleSaved, onDraftChange]
  );

  const handleDelete = useCallback(async () => {
    if (deleteTargetId == null) return;
    setDeleting(true);
    try {
      if (isDraft) {
        onDraftChange?.(
          draftRules.filter((rule) => rule.id !== deleteTargetId)
        );
      } else {
        await deleteContractorAvailabilityRule(
          new AbortController(),
          deleteTargetId
        );
      }
      setSuccessMessage(t('availabilityDeleted', 'Availability rule deleted.'));
      setDeleteTargetId(null);
      setSelectedId(null);
      refreshList();
    } catch (err: unknown) {
      setErrorMessage(
        getApiErrorMessage(
          err,
          t('failedToDeleteRule', 'Failed to delete availability rule.')
        )
      );
    } finally {
      setDeleting(false);
    }
  }, [
    deleteTargetId,
    draftRules,
    isDraft,
    onDraftChange,
    refreshList,
    setErrorMessage,
    setSuccessMessage,
    t,
  ]);

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: t('refresh', 'Refresh'),
        icon: <ArrowClockwiseDashesColor />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'create',
        text: t('create', 'Create'),
        icon: <AddCircle24Regular />,
        disabled: !canEdit,
        onClick: () => openCreate(),
      },
      {
        key: 'edit',
        text: t('edit', 'Edit'),
        icon: <Edit24Regular />,
        disabled: !canEdit || selectedId == null,
        onClick: () => {
          if (selectedId != null) openEdit(selectedId);
        },
      },
    ],
    [canEdit, openCreate, openEdit, selectedId, t]
  );

  const paginationBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'delete',
        text: t('delete', 'Delete'),
        icon: <DeleteRegular />,
        disabled: !canEdit || selectedId == null,
        onClick: () => {
          if (selectedId != null) setDeleteTargetId(selectedId);
        },
      },
    ],
    [canEdit, selectedId, t]
  );

  const empty = useMemo(
    () => ({
      title: t('noAvailabilityYet', 'No availability rules yet!'),
      subtitle: t(
        'emptyAvailabilityHint',
        'Create the first availability rule for this contractor.'
      ),
      action: canEdit
        ? {
            text: t('addAvailability', 'Add availability'),
            onClick: () => openCreate(),
          }
        : undefined,
    }),
    [canEdit, openCreate, t]
  );

  const handleListError = useCallback(
    (error: Error) => {
      setErrorMessage(
        getApiErrorMessage(
          error,
          t('failedToLoadRules', 'Failed to load availability rules.')
        )
      );
    },
    [setErrorMessage, t]
  );

  const draftRule =
    isDraft && drawer?.mode === 'edit'
      ? draftRules.find((rule) => rule.id === drawer.id)
      : undefined;

  return (
    <div style={{ overflowX: 'auto' }}>
      <PagedDataGrid<ContractorAvailabilityListItem>
        ref={gridRef}
        columns={columns}
        commandBarItems={commandBarItems}
        paginationBarItems={paginationBarItems}
        selectionMode="single"
        pageSize={PAGE_SIZE}
        listHeigh={425}
        onSelectionChange={(item) => setSelectedId(item?.id ?? null)}
        onRowDoubleClicked={(item) => {
          if (canEdit) openEdit(item.id);
        }}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        ) => {
          if (isDraft || contractorId == null) {
            return pageDraft(draftRules, pageSize, pageNo);
          }
          const result = await getContractorAvailabilityRules(
            abortController,
            contractorId,
            { pageSize, pageNo, orderBy, search, filters }
          );
          return {
            ...result,
            items: result.items as ContractorAvailabilityListItem[],
          } as IPagedCollection<ContractorAvailabilityListItem>;
        }}
        onLoading={() => setIsInProgress(true)}
        onLoaded={() => setIsInProgress(false)}
        onError={handleListError}
        empty={empty}
      />

      <ContractorAvailabilityDrawer
        open={drawer != null}
        mode={drawer?.mode ?? 'create'}
        contractorId={contractorId}
        ruleId={drawer?.id ?? null}
        draftRule={draftRule}
        onDraftCreate={isDraft ? handleDraftCreate : undefined}
        onDraftUpdate={isDraft ? handleDraftUpdate : undefined}
        onClose={closeDrawer}
        onSaved={handleSaved}
      />

      <Dialog
        open={deleteTargetId != null}
        onOpenChange={(_, data) => {
          if (!data.open && !deleting) setDeleteTargetId(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t('deleteAvailability', 'Delete availability rule?')}
            </DialogTitle>
            <DialogContent>
              {t(
                'deleteAvailabilityConfirm',
                'This availability rule will be removed.'
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteTargetId(null)}
                disabled={deleting}
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={handleDelete}
                disabled={deleting}
              >
                {t('delete', 'Delete')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ContractorAvailability;
